import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { teamHref } from '../lib/bowlWork.js'
import PageHeader from '../components/PageHeader.jsx'
import BowlTeamActions from '../features/bowl/BowlTeamActions.jsx'
import { BowlMutationProvider } from '../lib/bowlMutationContext.jsx'
import actionStyles from '../features/bowl/bowlActions.module.css'
import { api } from '../lib/api.js'
import { useAccess } from '../lib/access.jsx'
import { ACTIONS } from '../lib/rbac.js'
import {
  GROUPS, byGroup, reconciles, sortForGroup, freshness, nf, record,
  THREE_GROUPS_NOTE, CONTINGENT_NOTE, ELIMINATED_EMPTY_NOTE, MEMBERSHIP_NOTE,
  AS_OF_NOTE, RECONCILE_NOTE, NO_SNAPSHOT_NOTE, UNREADABLE_NOTE,
  NOT_A_CHAMPIONSHIP_NOTE, TRIGGER_NOTE, NO_TRIGGERS_NOTE, COVERAGE_NOTE, UNOWNED_NOTE,
  etTime, crossedCell, sourceStampCheck, landingLines, contingentLine,
} from '../lib/bowlEligibility.js'
import BowlFunnel from '../components/BowlFunnel.jsx'
import RepVolumeChart from '../features/bowl/RepVolumeChart.jsx'
import TeamDesk from '../features/bowl/TeamDesk.jsx'
import BowlDeskPanel from '../features/bowl/BowlDeskPanel.jsx'
import ReadinessNotice from '../features/bowl/ReadinessNotice.jsx'
import { readinessOf, assignmentSetupHref } from '../lib/assignmentSetup.js'
import { rollupState } from '../lib/bowlCoverage.js'
import {schoolMatchesMember, schoolMemberOptions, schoolTeamExport} from '../lib/schoolTeamView.js'
import repStyles from '../features/bowl/repVolume.module.css'
import { AgentBadge } from '../components/AgentBadge.jsx'
import BowlCommittees, { CommitteeSwitch } from '../features/bowl/BowlCommittees.jsx'
import styles from './BowlEligibility.module.css'

/**
 * BOWL ELIGIBILITY — Brian: "with FBS bowl eligible teams - create a place in
 * our command center that shows the bowl eligible teams."
 *
 * ⭐ THREE GROUPS, AND THAT IS THE DESIGN DECISION, NOT A FEATURE.
 * Asked literally, this page would show five teams in mid-October and 80 in
 * December, and in both months it would be read wrong: in October as a broken
 * screen, in December as a finished one. So it shows the whole board — in,
 * contingent, still alive, out — and the counts reconcile to the full 138-team
 * membership in front of the reader. A rep's actual question is not "who is
 * already in", it is "who is about to be", and that is the `still alive`
 * column sorted by how many wins they still need.
 *
 * ⭐ WHY `CONTINGENT` EXISTS AND WHY IT IS NOT HEDGING.
 * The bylaw counts at most one win over an FCS opponent, and only if that
 * opponent averaged at least 90% of the maximum permissible scholarships over
 * the prior two years. The NCAA does not publish which FCS programs clear
 * that. So for a team whose sixth win is an FCS win, nobody outside the NCAA
 * can evidence eligibility — Georgia, Indiana and James Madison are all in
 * that state right now, and the ordinary press convention calls all three
 * eligible. This page will not assert what it cannot show, and it says so in
 * the group's own blurb rather than in a footnote.
 *
 * ⭐ THE AS-OF STAMP IS LOAD-BEARING. A stale standings page looks exactly
 * like a current one — that is how a first pass at this data read Alabama as
 * 5-0 off a Wikipedia infobox stamped the same week, missing a loss. So the
 * source's OWN stamp is printed, not the time the page was opened, and the
 * board's age is rendered as a word next to it.
 *
 * ⭐ IT IS A READ. It never writes. The one button here re-reads the source
 * and replaces this week's row — that exists because a path needing a terminal
 * is not a path, not because a rep should have to use it.
 */
/* ⚠️ HOISTED, AND THE REASON IS A GATE RATHER THAN TASTE.
 * Every section on this page is `card` plus this module's `.panel` inset.
 * Written inline as a template literal inside the `GROUPS.filter(...).map()`
 * below, check-clickable.mjs read the word "card" in a className inside a map
 * and reported the SECTION as an inert record row — "nothing in this row is
 * clickable". It is a section containing a table, not a row, so neither of the
 * gate's two remedies fits: it has no destination to give, and marking a
 * <section> aria-disabled would be a false statement to a screen reader. One
 * constant removes the misfire without weakening the gate, which still checks
 * every genuinely row-shaped element in the file. */
const PANEL = `card ${styles.panel}`

export default function BowlEligibility() {
  const { can, why, canManageAssignments, ctx } = useAccess()
  const allowed = can(ACTIONS.CC_VIEW)
  /* The split-conference selector shows for a viewer who may assign. The server
   * re-checks Manage Assignments standing and refuses machines regardless. */
  const canAssign = canManageAssignments === true
  const viewerName = ctx?.user?.name || ''
  /* ⭐ ARRIVING FROM THE WORKLIST: ?team=Memphis&do=email opens Memphis's group,
   * scrolls to its row and opens that action. A worklist order that dropped
   * the rep on the top of a 138-team board would be a dead end with a link. */
  const [params, setParams] = useSearchParams()
  const focusTeam = params.get('team') || ''
  const focusDo = params.get('do') || ''
  const [state, setState] = useState({ status: 'loading', data: null, error: '' })
  const canPick = canAssign && state.status === 'ready' && state.data?.canPick === true
  const [open, setOpen] = useState('alive')
  const [memberFilter, setMemberFilter] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [trig, setTrig] = useState({ status: 'loading', data: null, error: '' })
  /* Teams | Bowl committees (2026-10-26). The committee is a separate buyer
   * from the teams, so it is its own view off this tab, in the URL so a
   * worklist item can link straight to a bowl. Same search params as the
   * team focus above: one hook, two readers. */
  const view = params.get('view') === 'committees' ? 'committees' : 'teams'
  const setView = useCallback((v) => setParams((p) => {
    const n = new URLSearchParams(p)
    if (v === 'committees') n.set('view', 'committees'); else { n.delete('view'); n.delete('bowl') }
    return n
  }), [setParams])

  const pendingRead = useRef(null)
  const load = useCallback(() => {
    pendingRead.current?.abort()
    const ctl = new AbortController()
    pendingRead.current = ctl
    const observe = (setter, value) => { if (!ctl.signal.aborted) setter(value) }
    setState((s) => ({ ...s, status: s.data ? 'refreshing' : 'loading' }))
    api.fbsBowlEligibility({signal: ctl.signal})
      .then((d) => observe(setState, { status: 'ready', data: d, error: '' }))
      .catch((e) => observe(setState, {
        status: 'error', data: null,
        error: e?.body?.detail || e?.body?.error || e?.message || UNREADABLE_NOTE,
      }))
    /* A SEPARATE read, and its failure is reported separately. The board and
     * the trigger list answer different questions, and one of them being
     * unavailable must not make the other look empty. */
    api.fbsBowlTriggers({signal: ctl.signal})
      .then((d) => observe(setTrig, { status: 'ready', data: d, error: '' }))
      .catch((e) => observe(setTrig, {
        status: 'error', data: null,
        error: e?.body?.detail || e?.body?.error || e?.message || 'Could not load the trigger list.',
      }))
  }, [])

  useEffect(() => {
    if (allowed) load()
    return () => pendingRead.current?.abort()
  }, [allowed, load])

  const reread = useCallback(() => {
    setRefreshing(true)
    api.fbsBowlEligibilityRun()
      .then(() => load())
      .catch((e) => setState((s) => ({
        ...s, status: 'error',
        error: e?.body?.detail || e?.body?.error || e?.message || 'The re-read was refused.',
      })))
      .finally(() => setRefreshing(false))
  }, [load])

  /* ⚠️ THESE TWO HOOKS LIVE ABOVE THE PERMISSION RETURN BECAUSE THEY MUST.
   * They were first written next to the render code that uses them, which put
   * them after `if (!allowed) return` — so on a render where the viewer lacks
   * CC_VIEW they would not run, and React's hook order would differ between
   * renders. eslint's react-hooks/rules-of-hooks caught it. The explanation of
   * WHAT they do is at the point of use below; this is only where they have to
   * be declared. */
  const coverageByConference = useMemo(() => {
    const m = new Map()
    for (const t of trig.data?.triggers || []) {
      if (t.coverage?.scope !== 'school' && t.coverage && t.conference && !m.has(t.conference)) m.set(t.conference, t.coverage)
    }
    return m
  }, [trig.data])

  /* ⚠️ THE TEAM'S OWN COVERAGE WINS, AND WITHOUT THIS LINE THE BACKEND FIX IS
   * INVISIBLE ON THE ONE TEAM IT WAS BUILT FOR. The board now attaches
   * `coverage` per team, including school-scoped desks. But Notre Dame is also
   * a TRIGGER, so `coverageByConference` holds an FBS Independents entry — the
   * old, correct-at-the-time answer "no row in sheet.json" — and reading the
   * conference map first would shadow the specific answer with the general one
   * and render "No desk" next to a desk that exists. More specific beats more
   * general; the propagation stays only as the fallback for a payload that has
   * not got the per-team field yet. */
  const coverageFor = useCallback(
    (team) => team?.coverage || coverageByConference.get(team?.conference) || null,
    [coverageByConference],
  )

  /* Board teams by name, so a TRIGGER row (which carries no `work`) renders the
   * same actions as its board row. Hooks above the permission return. */
  const teamByName = useMemo(() => {
    const m = new Map()
    for (const t of state.data?.snapshot?.teams || []) m.set(t.name, t)
    return m
  }, [state.data])

  /* Open the arriving team's group once, then bring its row into view. */
  const [focused, setFocused] = useState('')
  useEffect(() => {
    if (!focusTeam || focused === focusTeam) return
    const t = teamByName.get(focusTeam)
    if (!t) return
    setFocused(focusTeam)
    /* The row itself scrolls into view (BowlTeamActions), re-anchoring while
     * the sections above it finish loading; this only opens its group. */
    if (t.group) setOpen(t.group)
  }, [focusTeam, focused, teamByName])

  // A saved trigger link still reaches the grouped row if that transition is
  // no longer returned. Only one occurrence owns the active form and anchor.
  const focusedRow = params.get('actionRow') === 'trigger'
    && (trig.data?.triggers || []).some(t => t.team === focusTeam) ? 'trigger' : 'group'
  const actionsFor = (name, conference, rowLocation = 'group') => {
    const bt = teamByName.get(name)
    if (!bt) return <span className={styles.muted}>Not on this week&apos;s board, so no actions.</span>
    return (
      <BowlTeamActions team={{ ...bt, conference: bt.conference || conference }}
        actorId={ctx?.user?.id || ctx?.user?.username || ''}
        season={state.data?.snapshot?.season} isExec={ctx?.role === 'exec' && canPick} viewerIsExec={ctx?.role === 'exec'} canPick={canPick} viewerName={viewerName}
        deskVersion={state.data?.deskMeta?.version} workMeta={state.status === 'ready' ? state.data?.workMeta : null}
        rowLocation={rowLocation} onChanged={load}
        focus={focusTeam === name && rowLocation === focusedRow ? focusDo : ''} />
    )
  }

  if (!allowed) {
    return (
      <>
        <PageHeader eyebrow="College Football" title="Bowl Eligibility" />
        <div className={PANEL}>
          <p>{why?.(ACTIONS.CC_VIEW) || 'You do not have permission to view this.'}</p>
        </div>
      </>
    )
  }

  if (view === 'committees') {
    return (
      <>
        <PageHeader
          eyebrow="College Football"
          title="Bowl Eligibility"
          lead="Every bowl as a buyer: who is assigned the committee, who rides second, and the next step of the press before selection day."
        />
        <CommitteeSwitch view={view} onChange={setView} />
        <section className={PANEL}>
          <BowlCommittees />
        </section>
      </>
    )
  }

  const d = state.data
  const snap = d?.snapshot || null
  /* deskMeta carries no `ready`; only its errors are classified here. */
  const deskReadiness = readinessOf({ ...(d?.deskMeta || {}), ready: true })
  const filteredTeams = (snap?.teams || []).filter(t => schoolMatchesMember(t, memberFilter))
  const groups = byGroup(filteredTeams)
  const exportTeams = () => {
    const url = URL.createObjectURL(new Blob([schoolTeamExport(filteredTeams,snap.season)],{type:'text/csv;charset=utf-8'}))
    const link = document.createElement('a')
    link.href = url; link.download = `bowl-school-teams-${snap.season}.csv`; link.click()
    URL.revokeObjectURL(url)
  }
  const counts = snap?.counts || {}
  const fresh = snap ? freshness(snap.composedAt) : null
  const adds = snap ? reconciles(snap) : false

  /* ⭐ WHOSE DESK IS THIS TEAM ON — and the only honest route to it from here.
   *
   * 🔴 THE BOARD PAYLOAD CARRIES NO OWNER ON A TEAM. Measured live 2026-10-17:
   * across all 138 team records the union of keys is name, conference,
   * division, wins, losses, conferenceRecord, remaining, needs, fcsWins, group,
   * why, transitional, unconditional. Not one owner-shaped field. So a naive
   * "assigned rep" column here would be 138 blanks — which is the exact defect
   * just fixed on three surfaces, and on THIS page the rep name had been
   * rendering in a fifth table column that sat off-screen at 390px.
   *
   * ⚠️ WHAT IS LEGITIMATELY AVAILABLE, AND WHY THIS IS INFERENCE AND NOT
   * INVENTION. The trigger list attaches a `coverage` object per trigger whose
   * `body` is a CONFERENCE and whose `why` is a statement ABOUT THAT
   * CONFERENCE — verbatim: "Southeastern Conference is covered by 2 reps in the
   * 2026-2027 breakdown." A statement about a conference applies to every team
   * in that conference, and BOTH SIDES OF THE JOIN ARE ALREADY IN THE PAYLOAD:
   * a trigger carries `conference: "SEC"` next to `coverage.body: "Southeastern
   * Conference"`, and every board team carries the same `conference` string. So
   * coverage is propagated from the trigger to its whole conference.
   *
   * ⚠️ AND IT DELIBERATELY DOES NOT GO FURTHER. The full conference→rep map
   * lives in the backend's `assignment/sheet.json` (221 bodies). Importing a
   * copy of it into src/ would hand the frontend a second, immediately-stale
   * copy of a hand-maintained source of truth, which is the one rule this
   * codebase does not bend. The result is that 4 of the board's 11 conferences
   * resolve today and 7 do not, and the UI below counts the rest as NOT
   * READABLE rather than as unowned. Attaching `coverage` to every team in the
   * snapshot — exactly as the trigger list already does — closes it with no
   * change on this page.
   *
   * `coverageByConference` and `coverageFor` are declared above, before the
   * permission return, because hook order has to be identical on every render. */

  return (
    <BowlMutationProvider meta={state.status === 'ready' ? {...state.data?.workMeta,season:state.data?.snapshot?.season} : null}>
    <>
      <PageHeader
        eyebrow="College Football"
        title="Bowl Eligibility"
        lead="Who has reached the six wins a bowl invitation needs, who still can, and who cannot. Read once a week from the NCAA's own standings table."
      />
      <CommitteeSwitch view={view} onChange={setView} />

      {/* FAILED — say what failed. Never a blank board that reads as "nobody is in". */}
      {state.status === 'error' && (
        <div className={PANEL}>
          <p className={styles.bad}>{UNREADABLE_NOTE}</p>
          <p className={styles.muted}>{state.error}</p>
        </div>
      )}

      {/* LOADING */}
      {state.status === 'loading' && (
        <div className={PANEL}><p className={styles.muted}>Reading the board…</p></div>
      )}

      {/* NO SNAPSHOT — the absence of a board, which is not an empty board. */}
      {state.status === 'ready' && !snap && (
        <div className={PANEL}>
          <p className={styles.muted}>{NO_SNAPSHOT_NOTE}</p>
          <p className={styles.muted}>{d?.detail}</p>
          <button className="btn" type="button" onClick={reread} disabled={refreshing}>
            {refreshing ? 'Reading…' : 'Read it now'}
          </button>
        </div>
      )}

      {snap && (
        <>
          {/* ⭐ THE ANSWER, FIRST, AT THE SIZE OF AN ANSWER.
             *
             * ⚠️ WHY THIS SECTION CHANGED SHAPE, 2026-10-15. Brian opened this
             * page on his phone and the verdict was "the user interface is
             * pretty terrible" — and he was right about the specific thing:
             * the page opened with a season string, three definition-list
             * rows of provenance and a paragraph of prose, and the number a
             * person came for was four scrolls down inside a group of
             * buttons. Provenance is load-bearing on this board and it is NOT
             * being demoted — the as-of stamp is still above the fold, still
             * before any team is named, and the freshness word still sits
             * beside it. What changes is which of the two is set at the size
             * of an answer and which is set at the size of a footnote.
             *
             * ⚠️ AND THE SECOND NUMBER IS NOT DECORATION. "In" alone would be
             * the press convention and this board refuses it: a team whose
             * sixth win was over an FCS opponent cannot be evidenced either
             * way (CONTINGENT_NOTE), so the headline carries both figures or
             * it is asserting something the engine cannot show. */}
          <section className={`${PANEL} ${styles.answer}`}>
            <p className={`eyebrow ${styles.answerEyebrow}`}>
              {snap.season} season · week ending {snap.weekKey}
            </p>
            <p className={styles.answerNum}>
              <b>{nf(counts.eligible ?? 0)}</b>
              <span className={styles.answerOf}>of {nf(snap.membership)} are in</span>
            </p>
            <p className={styles.answerSub}>
              {contingentLine(nf(counts.contingent ?? 0))} · {nf(counts.alive ?? 0)} can still get there · {nf(counts.eliminated ?? 0)} cannot
            </p>
            {/* ⚠️ THE NCAA'S STAMP IS QUOTED, AND OUR OWN READ TIME IS GIVEN IN
                EASTERN WITH THE RIGHT ZONE (2026-11-01). The page printed
                "09:16 AM EDT" the day daylight saving ended, and on every week
                measured that stamp is LATER than when we read it, so its zone
                label is not Eastern. Saying so beats repeating it. */}
            {(() => {
              const sc = sourceStampCheck(snap.sourceStamp, snap.composedAt)
              return (
                <p className={styles.answerStamp}>
                  NCAA page stamp <b>&ldquo;{sc.text}&rdquo;</b> · read by us {etTime(snap.composedAt, { withDay: true })}
                  {fresh && <> · <span className={styles[`lvl_${fresh.level}`]}>{fresh.text}</span></>}
                  {d?.stale && <> · <span className={styles.tag}>stale</span></>}
                  {d?.durable === false && <> · <span className={styles.tag}>not durable</span></>}
                  {sc.warn && <><br /><span className={styles.muted}>{sc.warn}</span></>}
                </p>
              )
            })()}
            {/* WHO REFRESHED THIS WEEK'S BOARD - server-stamped. The badge is
                the landing that last CHANGED the board; every landing this week,
                including a no-change re-read, is listed under it so a person's
                re-read can no longer pass for the engine's run (2026-11-01). */}
            <p className={styles.answerStamp}>
              Refreshed by{' '}
              <AgentBadge agent={snap.landedBy?.agent} actor={snap.landedBy?.actor} at={snap.composedAt} />
              {landingLines(snap).length > 0 && (
                <><br /><span className={styles.muted}>{landingLines(snap).join(' · ')}</span></>
              )}
            </p>
            <div className={styles.answerActions}>
              <button className="btn" type="button" onClick={reread} disabled={refreshing}>
                {refreshing ? 'Reading…' : 'Re-read now'}
              </button>
            </div>

            {/* The rest of the provenance, one tap behind the answer. It is
               not hidden — a closed <details> is still read by a screen
               reader and still found by in-page search. */}
            <details className={styles.more}>
              <summary>Where this came from</summary>
              <dl className={styles.stamp}>
                <div>
                  <dt>Source</dt>
                  <dd>
                    <a href={snap.sourceUrl} target="_blank" rel="noreferrer noopener">
                      NCAA.com FBS standings
                    </a>
                  </dd>
                </div>
                <div>
                  <dt>Board composed</dt>
                  <dd>{etTime(snap.composedAt, { withDay: true })}</dd>
                </div>
              </dl>
              <p className={styles.muted}>{AS_OF_NOTE}</p>
            </details>
          </section>

          {/* ---- 📊 VOLUME PER REP, AT THE TOP ----
             *
             * Brian: "a chart at the top that shows volume per rip [rep]."
             *
             * ⚠️ IT SITS SECOND, NOT FIRST, AND THAT IS THE ONE PLACE IT CAN
             * GO. The section above is this page's single answer — how many
             * teams are in, with the as-of stamp that makes the whole board
             * trustworthy — and the standing rule is one answer at the top of a
             * screen. Two competing headline numbers in the first viewport is
             * the condition where neither gets read. So the board's answer
             * keeps the top and the book's answer is immediately under it,
             * above the four counts and well above the funnel.
             *
             * It takes BOTH read states because it depends on both: the teams
             * come from the board and the coverage comes from the trigger list.
             * If the trigger read failed, that is a PARTIAL chart and it says
             * so — it must not render as "nobody owns anything". */}
          <section className={PANEL}>
            <label className={repStyles.pickLabel}>Filter school rows by person
              <select value={memberFilter} onChange={event => setMemberFilter(event.target.value)}>
                <option value="">All people</option>
                {schoolMemberOptions(snap.teams || []).map(name => <option key={name} value={name}>{name}</option>)}
              </select>
            </label>
            <p className={styles.muted}>{filteredTeams.length} schools match. Summary counts show the whole board; school rows and export follow this filter.</p>
            <button className="btn" type="button" onClick={exportTeams}>Export filtered school teams</button>
            <RepVolumeChart
              status={state.status === 'error' ? 'error' : state.status === 'loading' ? 'loading' : 'ready'}
              teams={snap.teams || []}
              membership={snap.membership}
              coverageFor={coverageFor}
              coverageStatus={trig.status}
              assignmentReadiness={state.status === 'ready' && Array.isArray(d?.deskMeta?.errors) ? deskReadiness.state : 'unavailable'}
              error={state.error}
              weekKey={snap.weekKey}
            />
          </section>

          {/* ---- 🧭 BOWL DESKS BY CONFERENCE (2026-10-26) ----
             * Brian: "the AAC still doesn't show as assigned in bowl." The
             * status is computed from each conference's SCHOOLS, so a
             * conference whose schools all have desks reads assigned, and a
             * split one offers the selector. */}
          <section className={PANEL}>
            <BowlDeskPanel rows={d?.conferenceDesks || null} canPick={canPick} expectedVersion={d?.deskMeta?.version} season={d?.snapshot?.season} onPicked={load} />
            {/* Post-V4.001: the missing roster is named as SETUP with its fix;
                any OTHER desk read failure is still said as a failure. */}
            {deskReadiness.rosterMissing && (
              <ReadinessNotice readiness={{ state: 'setup' }} isExec={ctx?.role === 'exec'}
                setupHref={assignmentSetupHref(teamByName.get(focusTeam)?.conference || '', `/bowl-eligibility?${params}`)}
                classes={{ bad: styles.bad, setup: actionStyles.setup, setupText: actionStyles.setupText, btn: `${actionStyles.btn} ${actionStyles.btnFix}` }} />
            )}
            {deskReadiness.otherErrors.length > 0 && (
              <p className={styles.bad}>Desk picks or the assignment roster could not be read: {deskReadiness.otherErrors.join(' · ')}. Defaults are shown.</p>
            )}
          </section>

          {/* ---- the four counts ---- */}
          <section className={PANEL}>
            <div className={styles.head}>
              <h2 className={styles.h}>The whole board</h2>
              <div className={styles.meta}>
                <span>{nf(snap.membership)} teams</span>
              </div>
            </div>
            {/* ⚠️ THE CONTROLS COME BEFORE THE EXPLANATION, AND THAT IS THE
               WHOLE CHANGE. THREE_GROUPS_NOTE is a good paragraph and it was
               the first thing under the heading, so on a phone the four
               buttons a rep taps sat below a block of prose on every single
               visit — read once, scrolled past forever after. It moves one
               tap behind the thing it explains. */}
            <div className={styles.cards}>
              {GROUPS.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  className={`${styles.cardItem} ${open === g.key ? styles.cardOn : ''}`}
                  aria-pressed={open === g.key}
                  onClick={() => setOpen(open === g.key ? '' : g.key)}
                >
                  <b>{nf(counts[g.key] ?? 0)}</b>
                  <span className={styles.cardLabel}>{g.label}</span>
                  <span className={styles.cardBlurb}>{g.blurb}</span>
                </button>
              ))}
            </div>

            {/* 🔴 THE RECONCILIATION, SHOWN RATHER THAN ASSERTED. */}
            <p className={adds ? styles.muted : styles.bad}>
              {adds
                ? `${nf(counts.eligible)} + ${nf(counts.contingent)} + ${nf(counts.alive)} + ${nf(counts.eliminated)} = ${nf(snap.membership)}.`
                : 'These groups do not sum to the membership. Do not use this board.'}
            </p>
            <p className={styles.muted}>
              Undefeated: <b>{nf(snap.reconciliation?.undefeated)}</b>
              {snap.reconciliation?.undefeatedTeams?.length
                ? ` — ${snap.reconciliation.undefeatedTeams.join(', ')}.`
                : '.'}
            </p>
            {/* ⚠️ THE SUM STAYS ON THE FACE; ONLY ITS FOOTNOTES FOLD. The line
               above is the board's own proof that it is not dropping teams,
               and a reconciliation one tap behind a disclosure is a
               reconciliation nobody reads. The three paragraphs explaining
               WHY the groups are shaped this way are a different thing and
               they fold. */}
            <details className={styles.more}>
              <summary>Why four groups, and what counts as membership</summary>
              <p className={styles.muted}>{THREE_GROUPS_NOTE}</p>
              <p className={styles.muted}>{RECONCILE_NOTE}</p>
              <p className={styles.muted}>{MEMBERSHIP_NOTE}</p>
            </details>
          </section>

          {/* ---- 🔔 THE TRIGGERS. The list outbound acts on, and the only
                   thing on this page with a date a KPI can count from. ---- */}
          <section className={PANEL}>
            <div className={styles.head}>
              <h2 className={styles.h}>
                Became eligible{trig.data?.triggers?.length ? ` · ${nf(trig.data.triggers.length)}` : ''}
              </h2>
            </div>
            <p className={styles.muted}>{TRIGGER_NOTE}</p>

            {trig.status === 'error' && (
              <>
                <p className={styles.bad}>Could not load the trigger list.</p>
                <p className={styles.muted}>{trig.error}</p>
              </>
            )}

            {trig.status === 'ready' && !trig.data?.triggers?.length && (
              <p className={styles.muted}>{NO_TRIGGERS_NOTE}</p>
            )}

            {trig.data?.triggers?.length > 0 && (
              <>
                {/* ⚠️ `cc-cards` IS NOT DECORATION — IT IS THE PHONE LAYOUT.
                   Measured on the live 2.114.0 bundle at 390px: all four
                   tables on this page were still laid out side-by-side, in an
                   `overflow-x` scroller, with the explanatory column pinned
                   off the right edge. The repo already had the answer —
                   styles/cc-mobile.css turns a `table.cc-cards` into labelled
                   cards under 640px — and it was wired to seven surfaces out
                   of twenty-five. The conversion is the two class names, the
                   `data-col="name"` that marks the row as a data row, and a
                   `data-label` per cell so the card is self-describing once
                   the header row stops being drawn. */}
                <div className={`${styles.scroll} cc-scrollcue cc-cards-wrap`}>
                  <table className={`${styles.table} cc-cards`}>
                    <thead>
                      <tr>
                        <th className={styles.sticky}>Team</th>
                        <th>State</th>
                        <th>Crossed</th>
                        <th className={styles.wide}>Whose desk</th>
                        <th className={styles.wide}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* ⭐ NO LONGER A READOUT (2026-10-26). Brian: "there is
                          no way to control any of it". Every row now carries
                          its actions: the pick, Email, Art, Quote, Win and the
                          fix for whatever blocks them. */}
                      {trig.data.triggers.filter(t => schoolMatchesMember(teamByName.get(t.team) || t, memberFilter)).map((t) => (
                        <tr key={t.team} data-bowl-team={t.team}
                          className={focusTeam === t.team ? actionStyles.hilite : undefined}>
                          <td className={styles.sticky} data-col="name" data-label="Team">
                            {/* The name is the row's door: it opens what this
                                team needs next, same link a worklist order uses. */}
                            <Link to={teamHref(t.team, teamByName.get(t.team)?.work)}><b>{t.team}</b></Link>
                            <span className={styles.pill}>{t.conference}</span>
                          </td>
                          <td data-label="State">
                            {t.state === 'eligible'
                              ? <span className={styles.pillOk}>eligible</span>
                              : <span className={styles.pill}>contingent</span>}
                          </td>
                          {/* ONE label per latch week (2026-11-01). The run time
                              is not the crossing: a Saturday-night run and the
                              Sunday run both saw Saturday's slate, and a
                              browser-local date split them across 10/31 and 11/1. */}
                          <td data-label="Crossed">
                            {(() => { const c = crossedCell(t); return c.text
                              ? <>{c.text}{c.firstSeen && <><br /><span className={styles.muted}>{c.firstSeen}</span></>}</>
                              : '—' })()}
                          </td>
                          {/* ⚠️ THIS CELL USED TO RENDER THE RAW REP NAMES AND
                              IS NOW THE SAME COMPONENT AS THE BOARD BELOW, FOR
                              TWO REASONS. It joined `coverage.reps` with a
                              comma, so "Phil" rendered alone when Brian's rule
                              is that Phil always means Phil AND Madison; and it
                              printed `no desk derivable` as a bare flag with
                              nowhere to go, which is a dead end. TeamDesk fixes
                              both and makes the trigger table and the team
                              board say the same thing the same way. */}
                          <td className={styles.wide} data-label="Whose desk">
                            {/* canPick false HERE because the Actions cell owns
                                the pick now; two pickers on one card would be
                                two doors to the same write. */}
                            <TeamDesk team={t} coverage={t.coverage} canPick={false} expectedVersion={d?.deskMeta?.version} season={d?.snapshot?.season} onPicked={load} />
                          </td>
                          <td className={styles.wide} data-label="Actions">{actionsFor(t.team, t.conference, 'trigger')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className={styles.muted}>{COVERAGE_NOTE}</p>
                {trig.data.unowned?.length > 0 && (
                  <p className={styles.bad}>
                    {UNOWNED_NOTE}{' '}
                    {trig.data.unowned.map((u) => u.team).join(', ')}.
                  </p>
                )}
              </>
            )}
          </section>

          {/* ---- 📊 THE FUNNEL. Immediately under the triggers, because the
                   trigger date is the clock every stage below is measured
                   from. Its own read, its own failure: if the funnel cannot be
                   computed the board above still renders, and vice versa. ---- */}
          <BowlFunnel />

          {/* ---- gaps: the only thing this engine asks a human for ---- */}
          {snap.gaps?.length > 0 && (
            <section className={PANEL}>
              <h2 className={styles.h}>Needs a human</h2>
              <p className={styles.muted}>
                These teams have reached six wins and the engine holds no sourced record of which
                of those wins were over FCS opponents. It will not guess either way.
              </p>
              <ul className={styles.gaps}>
                {snap.gaps.map((g) => (
                  <li key={g.team}><b>{g.team}</b> — {g.why}</li>
                ))}
              </ul>
            </section>
          )}

          {/* ---- the group a reader opened ---- */}
          {GROUPS.filter((g) => g.key === open).map((g) => {
            const list = sortForGroup(g.key, groups[g.key])
            return (
              <section className={PANEL} key={g.key}>
                <div className={styles.head}>
                  <h2 className={styles.h}>{g.label} · {nf(list.length)}</h2>
                </div>
                <p className={styles.muted}>{g.blurb}</p>
                {g.key === 'contingent' && <p className={styles.muted}>{CONTINGENT_NOTE}</p>}
                {/* ⚠️ SAID ONCE, HERE, RATHER THAN ON EVERY CARD. "Whose desk"
                    on these rows is CONFERENCE coverage, and the caveat that
                    goes with it — many-to-one, and not the school record's own
                    sr_assigned_rep — is the same sentence for every team in a
                    conference. Printed per card it was three identical lines
                    under all 138 names. It belongs to the section. */}
                <p className={styles.muted}>{COVERAGE_NOTE}</p>

                {list.length === 0 && (
                  <p className={styles.muted}>
                    {g.key === 'eliminated' ? ELIMINATED_EMPTY_NOTE : 'The request succeeded and the answer is none.'}
                  </p>
                )}

                {list.length > 0 && (
                  <div className={`${styles.scroll} cc-scrollcue cc-cards-wrap`}>
                    <table className={`${styles.table} cc-cards`}>
                      <thead>
                        <tr>
                          <th className={styles.sticky}>Team</th>
                          {/* 🔴 WHOSE DESK, AND IT IS THE SECOND COLUMN ON
                              PURPOSE. The rep was previously on this page only
                              on the trigger table, in a FIFTH column that sat
                              off-screen at 390px. Under 640px `cc-cards` stops
                              drawing the header row and moves each `data-label`
                              onto its own cell, so column order becomes the
                              CARD's reading order — which is why this sits
                              directly under the team name rather than at the
                              end of the row where it would be last on the card
                              too. */}
                          <th>Whose desk</th>
                          {/* ACTIONS THIRD, for the same card-order reason: at
                              390px the rep reads team, desk, then what to do. */}
                          <th className={styles.wide}>Actions</th>
                          <th>Conference</th>
                          <th>Record</th>
                          <th>Left</th>
                          <th>Needs</th>
                          <th className={styles.wide}>Why</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* ⭐ NO LONGER A READOUT (2026-10-26). Each team row
                            carries its own actions; nothing is invented, every
                            control lands on an existing flow or a server write. */}
                        {list.map((t) => (
                          <tr key={t.name} data-bowl-team={t.name}
                            className={focusTeam === t.name ? actionStyles.hilite : undefined}>
                            <td className={styles.sticky} data-col="name" data-label="Team">
                              <Link to={teamHref(t.name, t.work)}><b>{t.name}</b></Link>
                              {t.transitional && <span className={styles.pill}>moving up</span>}
                              {t.unconditional && <span className={styles.pillOk}>unconditional</span>}
                            </td>
                            <td data-label="Whose desk">
                              <TeamDesk team={t} coverage={coverageFor(t)} canPick={false} expectedVersion={d?.deskMeta?.version} season={d?.snapshot?.season} onPicked={load} />
                            </td>
                            <td className={styles.wide} data-label="Actions">{actionsFor(t.name, t.conference)}</td>
                            <td data-label="Conference">{t.conference}{t.division ? ` · ${t.division}` : ''}</td>
                            <td data-label="Record"><b>{record(t)}</b></td>
                            <td data-label="Games left" data-zero={t.remaining === 0 ? '1' : undefined}>{t.remaining}</td>
                            <td data-label="Wins needed">{t.needs == null ? '—' : t.needs}</td>
                            <td className={styles.wide} data-label="Why">{t.why}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )
          })}

          {/* ---- per conference ---- */}
          <section className={PANEL}>
            <h2 className={styles.h}>By conference</h2>
            <details className={styles.more}>
              <summary>How a conference block is read</summary>
              <p className={styles.muted}>
                The blocks are read one at a time and each is checked against its known membership
                before a single record is extracted. A short block is treated as truncated, not as a
                small conference.
              </p>
            </details>
            <div className={`${styles.scroll} cc-scrollcue cc-cards-wrap`}>
              <table className={`${styles.table} cc-cards`}>
                <thead>
                  <tr>
                    <th className={styles.sticky}>Conference</th>
                    <th>Teams</th>
                    <th>Desk</th>
                    <th>In</th>
                    <th>Contingent</th>
                    <th>Alive</th>
                    <th>Out</th>
                  </tr>
                </thead>
                <tbody>
                  {(snap.reconciliation?.byConference || []).map((c) => (
                    <tr key={c.name} aria-disabled="true">
                      <td className={styles.sticky} data-col="name" data-label="Conference">{c.name}</td>
                      <td data-label="Teams">{c.teams}</td>
                      <td data-label="Desk">
                        {(() => {
                          const cd = (d?.conferenceDesks || []).find((x) => x.conference === c.name)
                          const st = rollupState(cd?.status)
                          return (
                            <span className={`${repStyles.rollupPill} ${repStyles[`rollup_${st.tone}`]}`} title={cd?.line || st.why}>
                              {st.label}{cd ? ` · ${cd.withDesk}/${cd.teams}` : ''}
                            </span>
                          )
                        })()}
                      </td>
                      <td data-label="In" data-zero={c.eligible === 0 ? '1' : undefined}>{c.eligible}</td>
                      <td data-label="Contingent" data-zero={c.contingent === 0 ? '1' : undefined}>{c.contingent}</td>
                      <td data-label="Alive">{c.alive}</td>
                      <td data-label="Out" data-zero={c.eliminated === 0 ? '1' : undefined}>{c.eliminated}</td>
                    </tr>
                  ))}
                </tbody>
                {/* ⚠️ THE TOTAL ROW CARRIES LABELS TOO. Under 640px the header
                   row is not drawn, so an unlabelled footer is six bare
                   numbers in a column — which is how a total becomes
                   indistinguishable from a conference nobody can name. */}
                <tfoot>
                  <tr className={styles.totalRow}>
                    <td className={styles.sticky} data-label="Row"><b>Total</b></td>
                    <td data-label="Teams"><b>{nf(snap.membership)}</b></td>
                    <td data-label="Desk">—</td>
                    <td data-label="In"><b>{nf(counts.eligible)}</b></td>
                    <td data-label="Contingent"><b>{nf(counts.contingent)}</b></td>
                    <td data-label="Alive"><b>{nf(counts.alive)}</b></td>
                    <td data-label="Out"><b>{nf(counts.eliminated)}</b></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          <section className={PANEL}>
            <h2 className={styles.h}>What this is, and what it is not</h2>
            <p className={styles.muted}>{NOT_A_CHAMPIONSHIP_NOTE}</p>
            <p className={styles.muted}>
              The bowl GAMES themselves — all 42 of them and the CFP National Championship, with
              their dates and the ring orders on both sides — live in the Bowl Season space inside
              the Command Center. This page is the other half of that question: which teams are
              heading there.
            </p>
          </section>
        </>
      )}
    </>
    </BowlMutationProvider>
  )
}
